import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';
import { formatCurrency as fmt } from '../utils/currency';
import { PRICING_TIER_OPTIONS, normalizePricingTier, pricingTierLabel } from '../utils/pricing';

// ── OptiKa palette ────────────────────────────────────────────────────────────

const C = {
  ink:     '#0B0E17',
  surface: '#1E2235',
  border:  '#2A2E45',
  muted:   '#8892A4',
  gold:    '#F0B429',
  teal:    '#00C9B1',
  coral:   '#FF6B6B',
  lavender:'#A78BFA',
  sky:     '#38BDF8',
  text:    '#E8EDF5',
  sub:     '#C5CDD8',
};

// ── CSS ───────────────────────────────────────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap');

  .cu-page * { box-sizing: border-box; }

  .cu-page {
    font-family: 'Cabinet Grotesk', 'Inter', system-ui, sans-serif;
    background: ${C.ink};
    min-height: 100vh;
    color: ${C.text};
  }

  .cu-mono { font-family: 'JetBrains Mono', monospace; }

  .cu-search {
    flex: 1; min-width: 220px;
    padding: 9px 14px; border-radius: 10px;
    border: 1.5px solid ${C.border}; font-size: 13px;
    background: rgba(255,255,255,0.04); color: ${C.text};
    outline: none; transition: border-color 0.2s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .cu-search:focus { border-color: ${C.gold}; }
  .cu-search::placeholder { color: ${C.muted}; }

  .cu-btn-primary {
    padding: 9px 18px; border-radius: 10px; border: none;
    background: ${C.gold}; color: #000;
    font-size: 13px; font-weight: 700; cursor: pointer;
    transition: background 0.18s; white-space: nowrap;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .cu-btn-primary:hover { background: #E0A520; }
  .cu-btn-primary:disabled { background: #3A3F55; color: ${C.muted}; cursor: not-allowed; }

  .cu-btn-outline {
    padding: 6px 13px; border-radius: 8px;
    border: 1px solid ${C.border};
    background: transparent; color: ${C.muted};
    font-size: 12px; font-weight: 600; cursor: pointer;
    transition: all 0.15s; white-space: nowrap;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .cu-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }

  .cu-btn-danger {
    padding: 6px 13px; border-radius: 8px;
    border: 1px solid rgba(255,107,107,0.3);
    background: rgba(255,107,107,0.08); color: ${C.coral};
    font-size: 12px; font-weight: 600; cursor: pointer;
    transition: all 0.15s; white-space: nowrap;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .cu-btn-danger:hover { background: rgba(255,107,107,0.18); border-color: ${C.coral}; }

  .cu-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .cu-table th {
    padding: 11px 16px; text-align: left; font-weight: 700;
    font-size: 11px; letter-spacing: 0.04em;
    background: rgba(240,180,41,0.07);
    color: ${C.gold}; border-bottom: 1px solid ${C.border};
    white-space: nowrap;
  }
  .cu-table td {
    padding: 12px 16px; border-bottom: 1px solid rgba(42,46,69,0.5);
    color: ${C.sub}; vertical-align: middle;
  }
  .cu-table tr:hover td { background: rgba(255,255,255,0.02); }

  .cu-badge {
    display: inline-block; padding: 3px 9px; border-radius: 20px;
    font-size: 11px; font-weight: 700; white-space: nowrap;
  }

  .cu-empty {
    text-align: center; padding: 72px 24px; color: ${C.muted}; font-size: 14px;
  }

  .cu-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.65);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
  }

  .cu-modal {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 16px; padding: 28px;
    width: 100%; max-width: 540px;
    max-height: 92vh; overflow-y: auto;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  }
  .cu-modal::-webkit-scrollbar { width: 4px; }
  .cu-modal::-webkit-scrollbar-track { background: transparent; }
  .cu-modal::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }

  .cu-form-label {
    display: block; font-size: 11px; font-weight: 700;
    color: ${C.muted}; letter-spacing: 0.07em;
    text-transform: uppercase; margin-bottom: 7px;
  }

  .cu-form-input {
    width: 100%; padding: 9px 13px; border-radius: 9px;
    border: 1.5px solid ${C.border}; font-size: 13px;
    color: ${C.text}; background: rgba(255,255,255,0.04);
    outline: none; transition: border-color 0.2s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .cu-form-input:focus { border-color: ${C.gold}; }
  .cu-form-input option { background: #1E2235; }

  .cu-alert-error {
    background: rgba(255,107,107,0.12); border: 1px solid rgba(255,107,107,0.3);
    color: ${C.coral}; border-radius: 9px; padding: 10px 14px;
    font-size: 13px; margin-bottom: 16px;
  }

  @keyframes cu-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes cu-fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
`;

// ── Badge components ──────────────────────────────────────────────────────────

const SEGMENT_META = {
  vip:      { label: 'VIP',      bg: 'rgba(240,180,41,0.15)',  color: '#F0B429', icon: '⭐' },
  regular:  { label: 'Regular',  bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8', icon: '🥈' },
  atRisk:   { label: 'At Risk',  bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B', icon: '⚠️' },
  inactive: { label: 'Inactive', bg: 'rgba(255,255,255,0.06)', color: '#8892A4', icon: '😴' },
  new:      { label: 'New',      bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', icon: '🌱' },
};

function SegmentBadge({ segment }) {
  const m = SEGMENT_META[segment] || SEGMENT_META.new;
  return (
    <span className="cu-badge" style={{ background: m.bg, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
}

function PricingBadge({ pricingTier }) {
  const tier = normalizePricingTier(pricingTier);
  const tones = {
    retail:    { bg: 'rgba(167,139,250,0.12)', color: '#A78BFA' },
    wholesale: { bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1' },
    vip:       { bg: 'rgba(240,180,41,0.12)',   color: '#F0B429' },
  };
  const tone = tones[tier] || tones.retail;
  return (
    <span className="cu-badge" style={{ background: tone.bg, color: tone.color }}>
      {pricingTierLabel(tier)}
    </span>
  );
}

function BirthdayCake({ birthday }) {
  if (!birthday) return null;
  const b = new Date(birthday);
  const today = new Date();
  const next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  const days = Math.ceil((next - today) / 86_400_000);
  if (days > 30) return null;
  return <span title={`Birthday in ${days} day${days !== 1 ? 's' : ''}`} style={{ marginLeft: 4 }}>🎂</span>;
}

function SpinIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" style={{ animation: 'cu-spin 0.8s linear infinite' }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const EMPTY = { name: '', email: '', phone: '', address: '', notes: '', birthday: '', pricingTier: 'retail' };

export default function Customers() {
  const [customers, setCustomers]   = useState([]);
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError]   = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setPageLoading(true);
    setPageError('');
    try {
      const { data } = await api.get('/customers', { params: { search } });
      setCustomers(data);
    } catch {
      setPageError('Failed to load customers. Please try again.');
    } finally {
      setPageLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setError(''); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name:        c.name,
      email:       c.email    || '',
      phone:       c.phone    || '',
      address:     c.address  || '',
      notes:       c.notes    || '',
      birthday:    c.birthday ? new Date(c.birthday).toISOString().split('T')[0] : '',
      pricingTier: normalizePricingTier(c.pricingTier),
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const payload = { ...form, birthday: form.birthday || null };
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
      } else {
        await api.post('/customers', payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this customer?')) return;
    try {
      await api.delete(`/customers/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete customer');
    }
  };

  return (
    <Layout>
      <style>{STYLES}</style>

      <div className="cu-page">
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 24px' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: 0, marginBottom: 4 }}>Customers</h1>
              <div style={{ fontSize: 13, color: C.muted }}>
                {customers.length} customer{customers.length !== 1 ? 's' : ''} ·{' '}
                <a href="/crm" style={{ color: C.gold, fontWeight: 600, textDecoration: 'none' }}>
                  View CRM →
                </a>
              </div>
            </div>
            <button className="cu-btn-primary" onClick={openNew}>+ Add Customer</button>
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="cu-search"
              placeholder="Search by name, email, or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Table card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
            {pageLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: C.muted }}>
                <SpinIcon size={22} />
                <span style={{ fontSize: 13 }}>Loading customers…</span>
              </div>
            ) : pageError ? (
              <div className="cu-empty">
                <div style={{ color: C.coral, marginBottom: 12 }}>{pageError}</div>
                <button className="cu-btn-outline" onClick={load}>Retry</button>
              </div>
            ) : customers.length === 0 ? (
              <div className="cu-empty">
                <div style={{ fontSize: 48, marginBottom: 14 }}>👥</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 6 }}>No customers yet</div>
                <div style={{ marginBottom: 16 }}>Start building your customer list</div>
                <button className="cu-btn-primary" onClick={openNew}>Add your first customer</button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="cu-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Segment</th>
                      <th>Pricing</th>
                      <th>Points</th>
                      <th>Total Spent</th>
                      <th>Last Purchase</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => {
                      const daysSince = c.lastPurchaseAt
                        ? Math.floor((Date.now() - new Date(c.lastPurchaseAt)) / 86_400_000)
                        : null;
                      return (
                        <tr key={c.id}>
                          <td>
                            <div style={{ fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                              {c.name}
                              <BirthdayCake birthday={c.birthday} />
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 13, color: C.sub }}>{c.phone || '—'}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>{c.email || ''}</div>
                          </td>
                          <td><SegmentBadge segment={c.segment} /></td>
                          <td><PricingBadge pricingTier={c.pricingTier} /></td>
                          <td>
                            <span className="cu-mono" style={{ fontWeight: 700, color: c.loyaltyPoints > 0 ? C.gold : C.border }}>
                              {c.loyaltyPoints ?? 0}
                            </span>
                          </td>
                          <td>
                            <span className="cu-mono" style={{ fontWeight: 600, color: C.teal }}>
                              {fmt(c.totalSpent || 0)}
                            </span>
                          </td>
                          <td>
                            {c.lastPurchaseAt ? (
                              <span style={{ fontSize: 12,
                                color: daysSince > 60 ? C.coral : daysSince > 30 ? C.gold : C.sub }}>
                                {new Date(c.lastPurchaseAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {daysSince > 60 && <span style={{ marginLeft: 4, fontSize: 10, color: C.muted }}>({daysSince}d ago)</span>}
                              </span>
                            ) : <span style={{ color: C.border, fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ fontSize: 12, color: C.muted }}>
                            {new Date(c.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="cu-btn-outline" onClick={() => openEdit(c)}>Edit</button>
                              <button className="cu-btn-danger" onClick={() => handleDelete(c.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="cu-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="cu-modal" style={{ animation: 'cu-fadeUp 0.2s ease' }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, margin: 0 }}>
                {editing ? 'Edit Customer' : 'Add Customer'}
              </h3>
              <button className="cu-btn-outline" onClick={() => setShowModal(false)} style={{ padding: '5px 10px' }}>✕</button>
            </div>

            {error && <div className="cu-alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label className="cu-form-label">Name *</label>
                <input className="cu-form-input" value={form.name} onChange={set('name')} required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label className="cu-form-label">Email</label>
                  <input className="cu-form-input" type="email" value={form.email} onChange={set('email')} />
                </div>
                <div>
                  <label className="cu-form-label">Phone</label>
                  <input className="cu-form-input" value={form.phone} onChange={set('phone')} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="cu-form-label">Address</label>
                <input className="cu-form-input" value={form.address} onChange={set('address')} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="cu-form-label">Pricing Tier</label>
                <select className="cu-form-input" value={form.pricingTier} onChange={set('pricingTier')}>
                  {PRICING_TIER_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  POS will use this price tier automatically when this customer is selected.
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="cu-form-label">Birthday (optional)</label>
                <input className="cu-form-input" type="date" value={form.birthday} onChange={set('birthday')} />
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>We'll remind you to send wishes</div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="cu-form-label">Notes</label>
                <textarea className="cu-form-input" rows={3} value={form.notes} onChange={set('notes')}
                  style={{ resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="cu-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="cu-btn-primary" disabled={loading}>
                  {loading ? 'Saving…' : editing ? 'Save Changes' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
